import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Breadcrumbs,
  Link,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useAuthState, useDataProvider, useNotify, useRedirect } from 'react-admin';
import { PageTitle } from '../../components/PageTitle';
import StoreIcon from '@mui/icons-material/Store';

interface BrandServiceTemplate {
  id: string;
  name: string;
  description?: string;
  basePrice?: number;
  estimatedDuration?: number;
  taxable: boolean;
  isActive: boolean;
  createdAt: string;
}

export const BrandServiceTemplateList = () => {
  const { identity } = useAuthState();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();

  // Load brand selection from localStorage on mount
  const [selectedBrandId, setSelectedBrandId] = useState<string>(() => {
    return localStorage.getItem('selectedBrandIdForTemplates') || '';
  });
  const [brands, setBrands] = useState<any[]>([]);
  const [templates, setTemplates] = useState<BrandServiceTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<BrandServiceTemplate | null>(null);
  const [selectedBrandName, setSelectedBrandName] = useState<string>('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    basePrice: '',
    estimatedDuration: '',
    taxable: true,
    isActive: true,
  });
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    basePrice: '',
    estimatedDuration: '',
    taxable: true,
    isActive: true,
  });
  const [submitting, setSubmitting] = useState(false);

  // Also check localStorage as fallback
  const storedUser = React.useMemo(() => {
    try {
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  }, []);

  const isEnterpriseAdmin =
    (identity as any)?.isEnterpriseAdmin === true ||
    storedUser?.isEnterpriseAdmin === true;

  // Fetch brands when component mounts
  React.useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await dataProvider.getList('brands', {
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'name', order: 'ASC' },
          filter: {},
        });
        setBrands(response.data);
      } catch (error) {
        console.error('Error fetching brands:', error);
        notify('Failed to load brands', { type: 'error' });
      }
    };

    if (isEnterpriseAdmin) {
      fetchBrands();
    }
  }, [isEnterpriseAdmin, dataProvider, notify]);

  // Fetch templates when brand is selected
  React.useEffect(() => {
    if (selectedBrandId) {
      const fetchTemplates = async () => {
        setLoading(true);
        try {
          // Note: This endpoint needs to be created in the backend
          const response = await dataProvider.getList('brand-service-templates', {
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'name', order: 'ASC' },
            filter: { brandId: selectedBrandId },
          });
          setTemplates(response.data);
        } catch (error) {
          console.error('Error fetching templates:', error);
          notify('Failed to load service templates', { type: 'error' });
          setTemplates([]);
        } finally {
          setLoading(false);
        }
      };

      fetchTemplates();
    }
  }, [selectedBrandId, dataProvider, notify]);

  const handleBrandChange = (brandId: string) => {
    setSelectedBrandId(brandId);
    localStorage.setItem('selectedBrandIdForTemplates', brandId);
    setTemplates([]);

    // Update brand name for breadcrumbs
    const brand = brands.find(b => b.id === brandId);
    setSelectedBrandName(brand?.name || '');
  };

  const handleCreate = () => {
    if (!selectedBrandId) {
      notify('Please select a brand first', { type: 'warning' });
      return;
    }
    setFormData({
      name: '',
      description: '',
      basePrice: '',
      estimatedDuration: '',
      taxable: true,
      isActive: true,
    });
    setCreateDialogOpen(true);
  };

  const handleFormChange = (field: string, value: any) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleCreateSubmit = async () => {
    // Validate required fields
    if (!formData.name.trim()) {
      notify('Name is required', { type: 'warning' });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        brandId: selectedBrandId,
        name: formData.name,
        description: formData.description || undefined,
        basePrice: formData.basePrice ? parseFloat(formData.basePrice) : undefined,
        estimatedDuration: formData.estimatedDuration ? parseInt(formData.estimatedDuration) : undefined,
        taxable: formData.taxable,
        isActive: formData.isActive,
      };

      await dataProvider.create('brand-service-templates', { data: payload });
      notify('Service template created successfully', { type: 'success' });

      // Refresh templates list
      const response = await dataProvider.getList('brand-service-templates', {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'name', order: 'ASC' },
        filter: { brandId: selectedBrandId },
      });
      setTemplates(response.data);
      setCreateDialogOpen(false);
    } catch (error) {
      console.error('Error creating template:', error);
      notify('Failed to create service template', { type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRowClick = (template: BrandServiceTemplate) => {
    setSelectedTemplate(template);
    // Pre-fill the edit form with template data
    setEditFormData({
      name: template.name,
      description: template.description || '',
      basePrice: template.basePrice ? template.basePrice.toString() : '',
      estimatedDuration: template.estimatedDuration ? template.estimatedDuration.toString() : '',
      taxable: template.taxable,
      isActive: template.isActive,
    });
    setEditDialogOpen(true);
  };

  const handleEditFormChange = (field: string, value: any) => {
    setEditFormData({ ...editFormData, [field]: value });
  };

  const handleCloseDialog = () => {
    setEditDialogOpen(false);
    setSelectedTemplate(null);
  };

  const handleEditSubmit = async () => {
    // Validate required fields
    if (!editFormData.name.trim()) {
      notify('Name is required', { type: 'warning' });
      return;
    }

    if (!selectedTemplate) return;

    setSubmitting(true);
    try {
      const payload = {
        name: editFormData.name,
        description: editFormData.description || undefined,
        basePrice: editFormData.basePrice ? parseFloat(editFormData.basePrice) : undefined,
        estimatedDuration: editFormData.estimatedDuration ? parseInt(editFormData.estimatedDuration) : undefined,
        taxable: editFormData.taxable,
        isActive: editFormData.isActive,
      };

      await dataProvider.update('brand-service-templates', {
        id: selectedTemplate.id,
        data: payload,
        previousData: selectedTemplate,
      });
      notify('Service template updated successfully', { type: 'success' });

      // Refresh templates list
      const response = await dataProvider.getList('brand-service-templates', {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'name', order: 'ASC' },
        filter: { brandId: selectedBrandId },
      });
      setTemplates(response.data);
      handleCloseDialog();
    } catch (error) {
      console.error('Error updating template:', error);
      notify('Failed to update service template', { type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTemplate) return;

    try {
      await dataProvider.delete('brand-service-templates', { id: selectedTemplate.id });
      notify('Service template deleted successfully', { type: 'success' });
      setTemplates(templates.filter(t => t.id !== selectedTemplate.id));
      handleCloseDialog();
    } catch (error) {
      console.error('Error deleting template:', error);
      notify('Failed to delete service template', { type: 'error' });
    }
  };

  if (!isEnterpriseAdmin) {
    return (
      <Box>
        <PageTitle
          title="Brand Service Templates"
          subtitle="Manage service templates for brands"
          icon={<StoreIcon />}
        />
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography color="warning">
              Only enterprise administrators can manage brand service templates
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 3 }}
        aria-label="breadcrumb"
      >
        <Link
          color="inherit"
          sx={{ cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          onClick={() => redirect('/configuration/enterprise-admin')}
        >
          Configuration
        </Link>
        <Typography color="text.primary">Brand Service Templates</Typography>
        {selectedBrandName && <Typography color="text.primary">{selectedBrandName}</Typography>}
      </Breadcrumbs>

      <PageTitle
        title="Brand Service Templates"
        subtitle="Manage service templates for brands"
        icon={<StoreIcon />}
      />

      {/* Brand Selector */}
      <Card sx={{ mb: 3, mt: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <TextField
              select
              label="Select Brand"
              value={selectedBrandId}
              onChange={(e) => handleBrandChange(e.target.value)}
              sx={{ minWidth: 300 }}
              disabled={brands.length === 0}
            >
              <MenuItem value="">-- Choose a brand --</MenuItem>
              {brands.map((brand) => (
                <MenuItem key={brand.id} value={brand.id}>
                  {brand.name}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
              disabled={!selectedBrandId}
              sx={{ mt: 1 }}
            >
              Create Template
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Templates List */}
      {selectedBrandId && (
        <Card>
          <CardContent>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : templates.length === 0 ? (
              <Typography color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                No service templates found for this brand
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Base Price</TableCell>
                      <TableCell align="right">Duration (min)</TableCell>
                      <TableCell align="center">Taxable</TableCell>
                      <TableCell align="center">Active</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {templates.map((template) => (
                      <TableRow
                        key={template.id}
                        hover
                        onClick={() => handleRowClick(template)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: '#f5f5f5',
                          },
                        }}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>{template.name}</TableCell>
                        <TableCell>{template.description || '-'}</TableCell>
                        <TableCell align="right">
                          {template.basePrice ? `$${template.basePrice.toFixed(2)}` : '-'}
                        </TableCell>
                        <TableCell align="right">{template.estimatedDuration || '-'}</TableCell>
                        <TableCell align="center">
                          {template.taxable ? '✓' : '✗'}
                        </TableCell>
                        <TableCell align="center">
                          {template.isActive ? '✓' : '✗'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Edit Template Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Edit Service Template</Typography>
            <IconButton
              aria-label="close"
              onClick={handleCloseDialog}
              sx={{
                color: 'grey.500',
              }}
            >
              ✕
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              fullWidth
              label="Name *"
              value={editFormData.name}
              onChange={(e) => handleEditFormChange('name', e.target.value)}
              placeholder="e.g., Oil Change"
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Description"
              value={editFormData.description}
              onChange={(e) => handleEditFormChange('description', e.target.value)}
              placeholder="e.g., Engine oil and filter change"
            />
            <TextField
              fullWidth
              type="number"
              label="Base Price"
              value={editFormData.basePrice}
              onChange={(e) => handleEditFormChange('basePrice', e.target.value)}
              placeholder="e.g., 65.00"
              inputProps={{ step: '0.01' }}
            />
            <TextField
              fullWidth
              type="number"
              label="Estimated Duration (minutes)"
              value={editFormData.estimatedDuration}
              onChange={(e) => handleEditFormChange('estimatedDuration', e.target.value)}
              placeholder="e.g., 45"
              inputProps={{ step: '1' }}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Taxable
                </Typography>
                <TextField
                  fullWidth
                  select
                  value={editFormData.taxable}
                  onChange={(e) => handleEditFormChange('taxable', e.target.value === 'true')}
                >
                  <MenuItem value={true as any}>Yes</MenuItem>
                  <MenuItem value={false as any}>No</MenuItem>
                </TextField>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Active
                </Typography>
                <TextField
                  fullWidth
                  select
                  value={editFormData.isActive}
                  onChange={(e) => handleEditFormChange('isActive', e.target.value === 'true')}
                >
                  <MenuItem value={true as any}>Yes</MenuItem>
                  <MenuItem value={false as any}>No</MenuItem>
                </TextField>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleDelete} color="error" variant="outlined">
            Delete
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            disabled={submitting}
          >
            {submitting ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Template Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Create Service Template</Typography>
            <IconButton
              aria-label="close"
              onClick={() => setCreateDialogOpen(false)}
              sx={{
                color: 'grey.500',
              }}
            >
              ✕
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              fullWidth
              label="Name *"
              value={formData.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              placeholder="e.g., Oil Change"
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Description"
              value={formData.description}
              onChange={(e) => handleFormChange('description', e.target.value)}
              placeholder="e.g., Engine oil and filter change"
            />
            <TextField
              fullWidth
              type="number"
              label="Base Price"
              value={formData.basePrice}
              onChange={(e) => handleFormChange('basePrice', e.target.value)}
              placeholder="e.g., 65.00"
              inputProps={{ step: '0.01' }}
            />
            <TextField
              fullWidth
              type="number"
              label="Estimated Duration (minutes)"
              value={formData.estimatedDuration}
              onChange={(e) => handleFormChange('estimatedDuration', e.target.value)}
              placeholder="e.g., 45"
              inputProps={{ step: '1' }}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Taxable
                </Typography>
                <TextField
                  fullWidth
                  select
                  value={formData.taxable}
                  onChange={(e) => handleFormChange('taxable', e.target.value === 'true')}
                >
                  <MenuItem value={true as any}>Yes</MenuItem>
                  <MenuItem value={false as any}>No</MenuItem>
                </TextField>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Active
                </Typography>
                <TextField
                  fullWidth
                  select
                  value={formData.isActive}
                  onChange={(e) => handleFormChange('isActive', e.target.value === 'true')}
                >
                  <MenuItem value={true as any}>Yes</MenuItem>
                  <MenuItem value={false as any}>No</MenuItem>
                </TextField>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateSubmit}
            variant="contained"
            disabled={submitting}
          >
            {submitting ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
